import { useEffect, useState, useCallback } from "react";
import {
    useToast, Box, Text, Grid, Heading, Badge,
    Flex, Divider, Select, HStack, Input, Button,
    Spinner, Progress, Accordion, AccordionItem,
    AccordionButton, AccordionPanel, AccordionIcon
} from "@chakra-ui/react";
import { INFRACTION_CLASS_LABELS } from "../utils/formatters";

const PERIODS = [
    { label: '1 Dia',        value: '1d'     },
    { label: '1 Semana',     value: '7d'     },
    { label: '1 Mês',        value: '30d'    },
    { label: 'Personalizado', value: 'custom' },
];

const Algorithms = () => {
    const [metrics, setMetrics]   = useState([]);
    const [loading, setLoading]   = useState(true);
    const [period, setPeriod]     = useState('7d');
    const [from, setFrom]         = useState('');
    const [to, setTo]             = useState('');
    const toast = useToast();

    const fetchMetrics = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            let url = `/api/algorithms/metrics?period=${period}`;
            if (period === 'custom' && from && to) {
                url += `&from=${from}&to=${to}`;
            }
            const res = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!res.ok) throw new Error('Erro ao buscar métricas');
            const data = await res.json();
            setMetrics(data.algorithms || []);
        } catch (err) {
            toast({ title: err.message, status: 'error', duration: 3000, isClosable: true });
        } finally {
            setLoading(false);
        }
    }, [period, from, to, toast]);

    useEffect(() => {
        if (period !== 'custom') fetchMetrics();
    }, [period, fetchMetrics]);

    return (
        <Box p={6} flex="1" minH="100vh" bg="gray.100">
            <Flex align="center" justify="space-between" mb={6} flexWrap="wrap" gap={3}>
                <Heading size="lg">Métricas de Algoritmos</Heading>
                <HStack spacing={3} flexWrap="wrap">
                    <Select value={period} onChange={e => setPeriod(e.target.value)} w="160px" bg="white">
                        {PERIODS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </Select>
                    {period === 'custom' && (
                        <>
                            <Input type="date" value={from} onChange={e => setFrom(e.target.value)} bg="white" w="160px" />
                            <Input type="date" value={to}   onChange={e => setTo(e.target.value)}   bg="white" w="160px" />
                            <Button colorScheme="teal" onClick={fetchMetrics} isDisabled={!from || !to}>
                                Buscar
                            </Button>
                        </>
                    )}
                </HStack>
            </Flex>

            {loading ? (
                <Flex justify="center" mt={12}><Spinner size="xl" color="teal.500" /></Flex>
            ) : (
                <Grid templateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' }} gap={6}>
                    {metrics.map(alg => <AlgorithmCard key={alg.id} alg={alg} />)}
                </Grid>
            )}
        </Box>
    );
};

const AlgorithmCard = ({ alg }) => {
    const hasClasses   = alg.infractionClasses && alg.infractionClasses.length > 0;
    const cameraPercent = alg.totalCameras > 0
        ? (alg.activeCameras / alg.totalCameras) * 100
        : 0;

    return (
        <Box bg="white" borderRadius="lg" boxShadow="md" overflow="hidden">
            {/* Header */}
            <Box bg="teal.600" px={5} py={4}>
                <Text fontSize="md" fontWeight="bold" color="white">{alg.name}</Text>
                {alg.description && (
                    <Text fontSize="xs" color="teal.100" mt={1}>{alg.description}</Text>
                )}
            </Box>

            <Box px={5} py={4}>
                {/* Cameras */}
                <Text fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase" mb={2}>
                    Câmeras
                </Text>
                <Flex justify="space-between" align="center" mb={1}>
                    <Text fontSize="sm" color="gray.700">
                        <Text as="span" fontWeight="bold" fontSize="lg" color="teal.600">
                            {alg.activeCameras}
                        </Text>
                        /{alg.totalCameras} ativas
                    </Text>
                    <Badge colorScheme={alg.activeCameras > 0 ? 'green' : 'gray'}>
                        {alg.totalCameras === 0 ? 'Nenhuma' : `${Math.round(cameraPercent)}%`}
                    </Badge>
                </Flex>
                <Progress value={cameraPercent} size="sm" colorScheme="teal" borderRadius="full" mb={4} />

                <Divider mb={4} />

                {/* Infractions */}
                <Text fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase" mb={2}>
                    Infrações no Período
                </Text>
                <Text fontSize="3xl" fontWeight="bold" color="orange.500" mb={hasClasses ? 2 : 4}>
                    {alg.infractionCount.toLocaleString('pt-BR')}
                </Text>

                {hasClasses && (
                    <Accordion allowToggle mb={4}>
                        <AccordionItem border="none">
                            <AccordionButton px={0} py={1} _hover={{ bg: 'transparent' }}>
                                <Text fontSize="xs" color="teal.600" fontWeight="semibold" flex="1" textAlign="left">
                                    Ver por tipo de infração
                                </Text>
                                <AccordionIcon color="teal.600" />
                            </AccordionButton>
                            <AccordionPanel px={0} pt={2} pb={0}>
                                {alg.infractionClasses.map(ic => {
                                    const label = INFRACTION_CLASS_LABELS[ic.class] || ic.class;
                                    const pct   = alg.infractionCount > 0
                                        ? (ic.count / alg.infractionCount) * 100
                                        : 0;
                                    return (
                                        <Box key={ic.class} mb={2}>
                                            <Flex justify="space-between" mb={0.5}>
                                                <Text fontSize="xs" color="gray.700">{label}</Text>
                                                <Text fontSize="xs" fontWeight="semibold" color="gray.700">
                                                    {ic.count.toLocaleString('pt-BR')} ({Math.round(pct)}%)
                                                </Text>
                                            </Flex>
                                            <Progress value={pct} size="xs" colorScheme="orange" borderRadius="full" />
                                        </Box>
                                    );
                                })}
                            </AccordionPanel>
                        </AccordionItem>
                    </Accordion>
                )}

                <Divider mb={4} />

                {/* Batches */}
                <Text fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase" mb={3}>
                    Lotes
                </Text>
                <Grid templateColumns="1fr 1fr" gap={3}>
                    <Box bg="yellow.50" borderRadius="md" p={3} textAlign="center">
                        <Text fontSize="xl" fontWeight="bold" color="yellow.600">{alg.batchesPending}</Text>
                        <Text fontSize="xs" color="gray.600">Pendentes</Text>
                    </Box>
                    <Box bg="green.50" borderRadius="md" p={3} textAlign="center">
                        <Text fontSize="xl" fontWeight="bold" color="green.600">{alg.batchesCompleted}</Text>
                        <Text fontSize="xs" color="gray.600">Concluídos no período</Text>
                    </Box>
                </Grid>
            </Box>
        </Box>
    );
};

export default Algorithms;
